import { useCallback } from 'react';
import type { BacktrackAnswersSnapshot } from '../../types/shared';
import { buildLogicContext } from '../../surveyLogic/context/buildLogicContext';
import { logBackNavigation } from '../logger/navigationLogger';
import type { SurveyPaginationContext } from '../types';
import { scrollToFirstQuestionOfPage } from '../utils';
import { resolveBackTargetPageIndex } from '../utils/resolveBackTargetPageIndex';

export function useOnBack(context: SurveyPaginationContext) {
  const {
    currentPageIndex,
    setCurrentPageIndex,
    setPhase,
    trackPageTime,
    pages,
    values,
    paginationRefs,
    survey,
    bumpProgressRevision,
    customFieldValues,
  } = context;

  return useCallback(() => {
    const refs = paginationRefs.current;
    if (!refs || !survey) return;

    const allQuestions = pages.flatMap(page => page.questions);
    const logicContext = buildLogicContext(values, allQuestions, customFieldValues);
    const targetPageIndex = resolveBackTargetPageIndex(
      refs,
      pages,
      currentPageIndex,
      logicContext
    );

    if (targetPageIndex !== null) {
      logBackNavigation(currentPageIndex);
      trackPageTime(currentPageIndex);
      refs.backtrackCount += 1;

      for (let i = targetPageIndex + 1; i < pages.length; i += 1) {
        refs.skippedPageIndices.delete(i);
        refs.visitedPageIndices.delete(i);
      }

      refs.maxVisitedPageIndex = Math.min(refs.maxVisitedPageIndex, targetPageIndex);

      const answersSnapshot: BacktrackAnswersSnapshot = {};
      for (const question of pages[targetPageIndex]?.questions ?? []) {
        answersSnapshot[question.id] = values[question.id];
      }

      refs.backtrackSnapshots[targetPageIndex] = {
        fromPageIndex: currentPageIndex,
        snapshot: answersSnapshot,
      };

      setCurrentPageIndex(targetPageIndex);
      setPhase({ kind: 'content', pageIndex: targetPageIndex });
      bumpProgressRevision();
      scrollToFirstQuestionOfPage(pages, targetPageIndex);
      return;
    }

    if (currentPageIndex === 0 && survey?.introPage) {
      logBackNavigation(currentPageIndex);
      trackPageTime(currentPageIndex);
      setPhase({ kind: 'intro' });
      bumpProgressRevision();
    }
  }, [
    currentPageIndex,
    setCurrentPageIndex,
    setPhase,
    trackPageTime,
    pages,
    values,
    paginationRefs,
    survey,
    bumpProgressRevision,
    customFieldValues,
  ]);
}
